import localForage from 'localforage';

/**
 * 键值对存储服务
 */
export class KVStore {

  private prefix: string;

  constructor(prefix: string) {
    this.prefix = prefix;
  }

  /**
   * 存储单条数据
   * @param key 存储类型标识
   * @param item 要存储的数据项
   * @returns 存储成功返回true，失败返回false
   */
  async setItem<T>(key: string, item: T): Promise<boolean> {
    try {
      const storageKey = this.storageKey(key);
      await localForage.setItem(storageKey, item);
      return true;
    } catch (error) {
      console.error('Failed to save item to local storage:', error);
      return false;
    }
  }

  /**
   * 获取单条数据
   * @param key 存储类型标识
   * @returns 返回存储的数据项，不存在则返回null
   */
  async getItem<T>(key: string): Promise<T | null> {
    try {
      const storageKey = this.storageKey(key);
      return await localForage.getItem<T>(storageKey);
    } catch (error) {
      console.error('Failed to retrieve item from local storage:', error);
      return null;
    }
  }

  /**
   * 删除单条数据
   * @param key 数据ID
   */
  async removeItem(key: string): Promise<boolean> {
    try {
      const storageKey = this.storageKey(key);
      await localForage.removeItem(storageKey);
      return true;
    } catch (error) {
      console.error('Failed to delete item from local storage:', error);
      return false;
    }
  }

  private storageKey(key: string): string {
    return `${this.prefix}_${key}`;
  }

  /**
   * 获取所有数据项
   */
  async getAllItems<T>(): Promise<T[]> {
    try {
      const items: T[] = [];
      
      // 获取所有key并查找匹配前缀的项
      const keys = await localForage.keys();
      for (const storageKey of keys) {
        if (storageKey.startsWith(this.prefix)) {
          const item = await localForage.getItem<T>(storageKey);
          if (item) {
            items.push(item);
          }
        }
      }

      return items;

    } catch (error) {
      console.error('Failed to retrieve all items from local storage:', error);
      return [];
    }
  }

  /**
   * 获取数据项总数
   */
  async size(): Promise<number> {
    try {
      // 获取所有key并计数匹配前缀的项
      const keys = await localForage.keys();
      return keys.filter(storageKey => storageKey.startsWith(this.prefix)).length;
    } catch (error) {
      console.error('Failed to count items in local storage:', error);
      return 0;
    }
  }

  /**
   * 清除所有数据项
   */
  async clearAll(): Promise<boolean> {
    try {
      // 获取所有需要删除的key
      const keys = await localForage.keys();
      const keysToRemove = keys.filter(storageKey => storageKey.startsWith(this.prefix));
      
      // 删除所有匹配的key
      await Promise.all(keysToRemove.map(key => localForage.removeItem(key)));
      
      return true;
    } catch (error) {
      console.error('Failed to clear items from local storage:', error);
      return false;
    }
  }
}